import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { useNovaStore } from '../../store/useNovaStore';
import { callExtract } from '../../services/assistant/cimeToolsClient';
import { KeyStatusBar } from './KeyStatusBar';
import { NovaChatComposer, type DocumentPayload } from './NovaChatComposer';
import { FeeAgreementPreviewCard } from './FeeAgreementPreviewCard';
import type { ExtractedAgreement } from '../../lib/assistant/mapExtraction';
import { Spinner } from '../ui/Spinner';

interface Step {
  type: 'info' | 'error';
  text: string;
}

export function NovaModal() {
  const isOpen = useNovaStore((s) => s.isOpen);
  const close = useNovaStore((s) => s.close);
  const keyUnlocked = useNovaStore((s) => s.keyUnlocked);

  const [steps, setSteps] = useState<Step[]>([]);
  const [extracted, setExtracted] = useState<ExtractedAgreement | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [createdDossierId, setCreatedDossierId] = useState<string | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);

  // Focus modal on open
  useEffect(() => {
    if (isOpen) {
      modalRef.current?.focus();
    }
  }, [isOpen]);

  // Escape closes the modal
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSteps([]);
      setExtracted(null);
      setWarnings([]);
      setLoading(false);
      setCreatedDossierId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  async function handleDocument(doc: DocumentPayload) {
    setExtracted(null);
    setWarnings([]);
    setCreatedDossierId(null);
    setLoading(true);
    setSteps([{ type: 'info', text: "Analyse du document en cours…" }]);

    try {
      const result = await callExtract('', doc);
      setExtracted(result.extracted);
      setWarnings(result.warnings);
      setSteps((s) => [...s, { type: 'info', text: "Document analyse. Verifiez les champs ci-dessous." }]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      let display: string;
      if (msg === 'extraction-failed' || msg.includes("extraction")) {
        display = "Je n'ai pas pu lire ce document. Essayez un PDF avec du texte selectionnable.";
      } else if (msg === 'access-denied' || msg.includes("acces") || msg.includes("access")) {
        display = "Acces refuse : cle invalide ou expiree.";
      } else if (msg.startsWith('permission:')) {
        display = msg.replace('permission:', '').trim();
      } else {
        display = msg || "Une erreur inattendue s'est produite.";
      }
      setSteps((s) => [...s, { type: 'error', text: display }]);
    } finally {
      setLoading(false);
    }
  }

  function handleCreated(id: string) {
    setCreatedDossierId(id);
    toast.success("Convention créée avec succès.");
  }

  function handleCommitError(msg: string) {
    setSteps((s) => [...s, { type: 'error', text: msg }]);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-end p-4 pointer-events-none"
      aria-modal="true"
      role="dialog"
      aria-label="Nova — Assistant juridique"
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className="pointer-events-auto w-[420px] max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl border border-cime-border focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-cime-border shrink-0">
          <span className="font-semibold text-cime-text-primary text-base">Nova</span>
          <button
            type="button"
            onClick={close}
            aria-label="Fermer Nova"
            className="text-cime-text-muted hover:text-cime-text-primary transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Key status */}
        <div className="shrink-0">
          <KeyStatusBar />
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Steps log */}
          {steps.length > 0 && (
            <div className="px-4 pt-3 pb-1 space-y-1">
              {steps.map((step, i) => (
                <p
                  key={i}
                  className={
                    step.type === 'error'
                      ? 'text-xs text-red-600'
                      : 'text-xs text-cime-text-muted'
                  }
                >
                  {step.text}
                </p>
              ))}
            </div>
          )}

          {/* Loading indicator */}
          {loading && (
            <div className="flex items-center justify-center py-6">
              <Spinner />
            </div>
          )}

          {/* Success state */}
          {createdDossierId && (
            <div className="px-4 py-3">
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700 flex items-center gap-2">
                <span aria-hidden="true">✅</span>
                <span>
                  Convention créée avec succès.{' '}
                  <a
                    href={`/dossiers/${createdDossierId}/facturation`}
                    className="underline font-medium hover:opacity-80"
                    onClick={close}
                  >
                    Voir dans le dossier
                  </a>
                </span>
              </div>
            </div>
          )}

          {/* Extracted agreement form */}
          {extracted && !createdDossierId && (
            <div className="px-4 py-3">
              <FeeAgreementPreviewCard
                extracted={extracted}
                warnings={warnings}
                onCreated={handleCreated}
                onError={handleCommitError}
              />
            </div>
          )}

          {/* Drop zone — shown when no extracted result and not loading */}
          {!extracted && !loading && (
            <>
              {!keyUnlocked && (
                <div className="px-4 pt-4 pb-2">
                  <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700 text-center">
                    Déverrouillez votre clé pour analyser un document.
                  </div>
                </div>
              )}
              <NovaChatComposer
                disabled={!keyUnlocked}
                onDocument={handleDocument}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
